import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { createFPOInterest } from "@/lib/api";
import { CheckCircle2, ArrowRight, Users2, Handshake, Layers } from "lucide-react";
import { MAHARASHTRA_DISTRICTS } from "@/data/jalnaVillages";

const HERO = "/photos-r3b/fpo-leadership-meeting.jpg";

// FPO onboarding restricted to Maharashtra in Phase 1 (execution brief · LOW #2).
const INDIAN_STATES = ["Maharashtra"];

export default function FPOPartnership() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "", registration_no: "", contact_person: "", phone: "", email: "",
    village: "", taluka: "",
    district: "", state: "Maharashtra", member_count: "", commodities: "",
    annual_supply_capacity: "", notes: "", consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.contact_person || !form.phone || !form.district || !form.state) {
      toast.error(t("toast.fill_required"));
      return;
    }
    if (!form.consent) { toast.error(t("fpo.consent_required")); return; }
    setSubmitting(true);
    try {
      await createFPOInterest({
        fpo_name: form.name,
        registration_no: form.registration_no || null,
        contact_person: form.contact_person,
        phone: form.phone.replace(/\D/g, "").slice(-10),
        email: form.email || null,
        district: form.district,
        state: form.state,
        member_count: form.member_count ? parseInt(form.member_count) : null,
        commodities: form.commodities.split(",").map((s) => s.trim()).filter(Boolean),
        notes: [
          form.village || form.taluka ? `Village/Taluka: ${[form.village, form.taluka].filter(Boolean).join(", ")}` : null,
          form.annual_supply_capacity ? `Annual supply capacity: ${form.annual_supply_capacity}` : null,
          form.notes || null,
        ].filter(Boolean).join("\n") || null,
        consent: true,
        preferred_language: "en",
      });
      setDone(true);
      toast.success(t("toast.fpo_ok"));
    } catch (err) {
      toast.error(err?.response?.data?.detail?.error || t("toast.generic_error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-fpo">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Farm" className="w-full h-full object-cover" />
          <div className="absolute inset-0 tr-hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-28 lg:py-40 text-white">
          <div className="tr-eyebrow !text-[var(--tr-citrus-yellow)] before:!bg-[var(--tr-citrus-yellow)]">{t("fpo.hero_eyebrow")}</div>
          <h1 className="tr-rise mt-4 font-display font-extrabold text-5xl sm:text-6xl lg:text-8xl leading-[0.92] tracking-tight max-w-4xl">
            {t("fpo.hero_title_a")}<br /><span className="text-[var(--tr-citrus-yellow)]">{t("fpo.hero_title_b")}</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/85">{t("fpo.hero_body")}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { icon: Handshake, k: "offtake" },
          { icon: Users2, k: "cap" },
          { icon: Layers, k: "multi" },
        ].map((b) => (
          <div key={b.k} className="tr-card p-8">
            <b.icon className="w-6 h-6 text-[var(--tr-zest)]" />
            <div className="mt-4 font-display font-extrabold text-xl text-[var(--tr-deep-green)]">{t(`fpo.b_${b.k}_t`)}</div>
            <p className="mt-2 text-[var(--tr-muted)] leading-relaxed">{t(`fpo.b_${b.k}_d`)}</p>
          </div>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="tr-eyebrow">{t("fpo.form_eyebrow")}</div>
          <h2 className="tr-section-title text-4xl lg:text-5xl mt-4">{t("fpo.form_title_a")}<br />{t("fpo.form_title_b")}</h2>
        </div>
        <div className="lg:col-span-7">
          {done ? (
            <div data-testid="fpo-success" className="tr-card p-10 text-center">
              <CheckCircle2 className="w-14 h-14 text-[var(--tr-lime)] mx-auto" />
              <h3 className="font-display font-extrabold text-3xl mt-4 text-[var(--tr-deep-green)]">{t("fpo.success_title")}</h3>
              <p className="mt-3 text-[var(--tr-muted)]">{t("fpo.success_body")}</p>
            </div>
          ) : (
            <form data-testid="fpo-form" onSubmit={submit} className="tr-card p-8 lg:p-10 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label={`${t("fpo.f_name")}*`}><input required data-testid="fpo-name" className="tr-input" value={form.name} onChange={onChange("name")} /></Field>
                <Field label={t("fpo.f_regno")}><input data-testid="fpo-regno" className="tr-input" value={form.registration_no} onChange={onChange("registration_no")} /></Field>
                <Field label={`${t("fpo.f_contact")}*`}><input required data-testid="fpo-contact" className="tr-input" value={form.contact_person} onChange={onChange("contact_person")} /></Field>
                <Field label={`${t("fpo.f_phone")}*`}><input required inputMode="tel" data-testid="fpo-phone" className="tr-input" value={form.phone} onChange={onChange("phone")} /></Field>
                <Field label={t("fpo.f_email")}><input type="email" data-testid="fpo-email" className="tr-input" value={form.email} onChange={onChange("email")} /></Field>
                <Field label={t("fpo.f_members")}><input type="number" inputMode="numeric" data-testid="fpo-members" className="tr-input" value={form.member_count} onChange={onChange("member_count")} /></Field>
                <Field label={t("fpo.f_village")}><input data-testid="fpo-village" className="tr-input" value={form.village} onChange={onChange("village")} /></Field>
                <Field label={t("fpo.f_taluka")}><input data-testid="fpo-taluka" className="tr-input" value={form.taluka} onChange={onChange("taluka")} /></Field>
                <Field label={`${t("fpo.f_district")}*`}>
                  <select required data-testid="fpo-district" className="tr-input" value={form.district} onChange={onChange("district")}>
                    <option value="">— {t("fpo.f_district_select", "select Maharashtra district")} —</option>
                    {MAHARASHTRA_DISTRICTS.map((d) => <option key={d}>{d}</option>)}
                  </select>
                </Field>
                <Field label={`${t("fpo.f_state")}*`}>
                  <select required data-testid="fpo-state" className="tr-input" value={form.state} onChange={onChange("state")}>
                    {INDIAN_STATES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label={t("fpo.f_commodities")} className="md:col-span-2">
                  <input data-testid="fpo-commodities" className="tr-input" placeholder={t("fpo.f_commodities_ph")} value={form.commodities} onChange={onChange("commodities")} />
                </Field>
                <Field label={t("fpo.f_supply_capacity")} className="md:col-span-2">
                  <input data-testid="fpo-supply-capacity" className="tr-input" placeholder={t("fpo.f_supply_capacity_ph")} value={form.annual_supply_capacity} onChange={onChange("annual_supply_capacity")} />
                </Field>
                <Field label={t("fpo.f_notes")} className="md:col-span-2">
                  <textarea data-testid="fpo-notes" className="tr-input tr-textarea" value={form.notes} onChange={onChange("notes")} />
                </Field>
              </div>
              {/* DPDP Act consent — Round 3 Part B §3. Unticked by default, required. */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="fpo-consent"
                  checked={form.consent}
                  onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                  className="mt-1 w-5 h-5 accent-[var(--tr-deep-green)] flex-shrink-0"
                />
                <span className="text-sm text-[var(--tr-deep-green)] leading-relaxed">{t("fpo.f_consent")}</span>
              </label>
              <button type="submit" data-testid="fpo-submit" disabled={submitting} className="tr-pill-btn tr-btn-primary disabled:opacity-60">
                {submitting ? t("common.submitting") : t("fpo.f_submit")} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="tr-label">{label}</span>
      {children}
    </label>
  );
}
